import { Phone, Envelope, X as XIcon, Plus, LinkBreak, Info, ArrowRight } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { ConnectedInboxProvider, ConnectedInboxStatus, Supplier } from '@dill/dill-shared';
import { DillButton, DillInput } from '../../../../../general/Dill';
import {
  ConnectedInboxSummary,
  disconnectInboxThunk,
  fetchConnectedInboxesThunk,
  getInboxConnectUrlThunk,
} from '../../../../../../redux/services/emailAgentService';
import { openDillToast } from '../../../../../../utils/helpers';

type Tab = 'phone' | 'email';
type InboxProvider = ConnectedInboxProvider;

const PROVIDER_COPY: Record<
  InboxProvider,
  { label: string; hint: string; badgeClass: string; letter: string }
> = {
  GMAIL: {
    label: 'Gmail',
    hint: 'Gmail or Google Workspace',
    badgeClass: 'bg-red-50 text-red-600',
    letter: 'G',
  },
  OUTLOOK: {
    label: 'Outlook',
    hint: 'Outlook or Microsoft 365',
    badgeClass: 'bg-blue-50 text-blue-600',
    letter: 'O',
  },
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const STATUS_COPY: Record<ConnectedInboxStatus, { label: string; dot: string; text: string }> = {
  CONNECTED: { label: 'Connected', dot: 'bg-green-500', text: 'text-green-700' },
  NEEDS_RECONNECT: { label: 'Reconnect', dot: 'bg-amber-500', text: 'text-amber-700' },
  DISCONNECTED: { label: 'Disconnected', dot: 'bg-gray-400', text: 'text-gray-500' },
};

export const PhoneNumbersSettingsSection = ({ supplier }: { supplier?: Supplier | null }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);
  const [activeTab, setActiveTab] = useState<Tab>('email');
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [inboxes, setInboxes] = useState<ConnectedInboxSummary[]>([]);
  const [configuredProviders, setConfiguredProviders] = useState<
    Partial<Record<InboxProvider, boolean>>
  >({});
  const [newPhone, setNewPhone] = useState('');
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState<InboxProvider | null>(null);
  const [isConnectingInbox, setIsConnectingInbox] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [removingInboxId, setRemovingInboxId] = useState<string | null>(null);
  const [confirmingRemoveInboxId, setConfirmingRemoveInboxId] = useState<string | null>(null);
  const [isLoadingInboxes, setIsLoadingInboxes] = useState(false);

  const loadInboxes = async () => {
    if (!supplier?.id) return;
    setIsLoadingInboxes(true);
    const result = await dispatch(fetchConnectedInboxesThunk({ supplierId: supplier.id }));
    setIsLoadingInboxes(false);
    if (fetchConnectedInboxesThunk.fulfilled.match(result)) {
      setInboxes(result.payload.inboxes);
      setConfiguredProviders(result.payload.providers);
    }
  };

  useEffect(() => {
    loadInboxes();
  }, [supplier?.id]);

  // Coming back from the Google / Microsoft sign-in: report the outcome, then drop the params so
  // a refresh does not repeat the toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('inboxConnect');
    if (!outcome) return;
    if (outcome === 'success') {
      openDillToast({
        message: `Connected ${params.get('inboxEmail') || 'inbox'}.`,
        type: 'SUCCESS',
      });
    } else {
      openDillToast({
        message: params.get('reason') || 'Failed to connect the inbox.',
        type: 'ERROR',
      });
    }
    ['inboxConnect', 'inboxEmail', 'reason'].forEach((key) => params.delete(key));
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [location.search]);

  const handleAddPhone = () => {
    const trimmed = newPhone.trim();
    if (trimmed && !phoneNumbers.includes(trimmed)) {
      setPhoneNumbers([...phoneNumbers, trimmed]);
      setNewPhone('');
      setIsAddingPhone(false);
    }
  };

  const handleRemovePhone = (phone: string) => {
    setPhoneNumbers(phoneNumbers.filter((p) => p !== phone));
  };

  const handleStartConnectInbox = (provider: InboxProvider) => {
    setConnectingProvider(provider);
  };

  // The supplier names the inbox (e.g. their support address); we send the browser to the
  // provider's sign-in with that address pre-filled, and the backend rejects the connection if
  // they sign in to a different account. It comes back to this page.
  const handleConfirmConnectInbox = async (
    provider: InboxProvider | null = connectingProvider,
    email: string = newEmail
  ) => {
    const trimmed = email.trim().toLowerCase();
    if (!provider || !supplier?.id || !isValidEmail(trimmed)) return;
    if (
      provider === connectingProvider &&
      inboxes.some((i) => i.email === trimmed && i.status === 'CONNECTED')
    ) {
      openDillToast({ message: `${trimmed} is already connected.`, type: 'ERROR' });
      return;
    }
    setIsConnectingInbox(true);
    const result = await dispatch(
      getInboxConnectUrlThunk({
        supplierId: supplier.id,
        provider,
        email: trimmed,
        returnPath: location.pathname,
      })
    );
    if (getInboxConnectUrlThunk.fulfilled.match(result)) {
      window.location.assign(result.payload);
      return;
    }
    setIsConnectingInbox(false);
    openDillToast({
      message: (result.payload as string) || 'Failed to start sign-in.',
      type: 'ERROR',
    });
  };

  const handleRemoveInbox = async (inbox: ConnectedInboxSummary) => {
    if (!supplier?.id) return;
    setRemovingInboxId(inbox.id);
    const result = await dispatch(
      disconnectInboxThunk({ supplierId: supplier.id, inboxId: inbox.id })
    );
    setRemovingInboxId(null);
    setConfirmingRemoveInboxId(null);
    if (disconnectInboxThunk.fulfilled.match(result)) {
      setInboxes((prev) => prev.filter((i) => i.id !== inbox.id));
      openDillToast({ message: `Disconnected ${inbox.email}.`, type: 'SUCCESS' });
    } else {
      openDillToast({
        message: (result.payload as string) || 'Failed to disconnect inbox.',
        type: 'ERROR',
      });
    }
  };

  const handleCancelAddPhone = () => {
    setNewPhone('');
    setIsAddingPhone(false);
  };

  const handleCancelConnectInbox = () => {
    setNewEmail('');
    setConnectingProvider(null);
  };

  const isNewEmailValid = isValidEmail(newEmail.trim());

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Contact Information</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Connect inboxes and manage phone numbers for this supplier.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('email')}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent ${
            activeTab === 'email'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}>
          <Envelope size={13} />
          Email Addresses
          {inboxes.length > 0 && (
            <span className="ml-1 text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 leading-none">
              {inboxes.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('phone')}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent ${
            activeTab === 'phone'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}>
          <Phone size={13} />
          Phone Numbers
          {phoneNumbers.length > 0 && (
            <span className="ml-1 text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 leading-none">
              {phoneNumbers.length}
            </span>
          )}
        </button>
      </div>

      {/* Phone Numbers Tab */}
      {activeTab === 'phone' && (
        <div className="flex flex-col flex-1">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] text-gray-400">
              Available when making calls from the collection activity timeline.
            </p>
            {!isAddingPhone && (
              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                <DillButton
                  label="Add phone"
                  icon={<Plus size={13} />}
                  onClick={() => setIsAddingPhone(true)}
                  isDisabled={!canEdit}
                />
              </div>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Column Headers */}
            <div className="grid grid-cols-[1fr_80px_32px] bg-gray-50 px-4 py-2 border-b border-gray-200">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Phone Number
              </span>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </span>
              <span />
            </div>

            {/* Inline Add Row */}
            {isAddingPhone && (
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-blue-50/30">
                <div className="flex-1">
                  <DillInput
                    value={newPhone}
                    onChange={setNewPhone}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <DillButton
                  label="Save"
                  onClick={handleAddPhone}
                  isDisabled={!newPhone.trim() || !canEdit}
                />
                <DillButton label="Cancel" variant="secondary" onClick={handleCancelAddPhone} />
              </div>
            )}

            {/* Phone Number Rows */}
            {phoneNumbers.map((phone) => (
              <div
                key={phone}
                className="group grid grid-cols-[1fr_80px_32px] items-center px-4 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Phone size={13} className="text-gray-400 shrink-0" />
                  <span className="text-xs text-gray-800">{phone}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <span className="text-[11px] text-green-700">Active</span>
                </div>
                <div className="flex justify-end">
                  <span data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                    <button
                      onClick={() => handleRemovePhone(phone)}
                      disabled={!canEdit}
                      className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all cursor-pointer bg-transparent border-none disabled:cursor-not-allowed">
                      <XIcon size={13} />
                    </button>
                  </span>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {phoneNumbers.length === 0 && !isAddingPhone && (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-gray-400">No phone numbers added yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Addresses Tab */}
      {activeTab === 'email' && (
        <div className="flex flex-col flex-1">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] text-gray-400">
              Connect one or more inboxes for Dill AI to send from and monitor for replies.
            </p>
            {!connectingProvider && (
              <div className="relative">
                <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                  <DillButton
                    label="Connect inbox"
                    icon={<Plus size={13} />}
                    onClick={() => setConnectingProvider('GMAIL')}
                    isDisabled={!canEdit}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Column Headers */}
            <div className="grid grid-cols-[1fr_96px_120px] bg-gray-50 px-4 py-2 border-b border-gray-200">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Inbox
              </span>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </span>
              <span />
            </div>

            {/* Connect Inbox flow */}
            {connectingProvider && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleConfirmConnectInbox();
                }}
                className="flex flex-col gap-3 px-4 py-4 border-b border-gray-200 bg-gray-50/60">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-900">Connect an inbox</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Choose a provider, then enter the address Dill AI should monitor.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelConnectInbox}
                    disabled={isConnectingInbox}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-600 bg-transparent border-none p-0.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50">
                    <XIcon size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(PROVIDER_COPY) as InboxProvider[]).map((provider) => {
                    const isSelected = connectingProvider === provider;
                    return (
                      <button
                        key={provider}
                        type="button"
                        onClick={() => handleStartConnectInbox(provider)}
                        disabled={isConnectingInbox}
                        aria-pressed={isSelected}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border bg-white text-left cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? 'border-blue-500 ring-1 ring-blue-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${PROVIDER_COPY[provider].badgeClass}`}>
                          {PROVIDER_COPY[provider].letter}
                        </span>
                        <span className="flex flex-col flex-1 min-w-0">
                          <span className="text-xs font-medium text-gray-900">
                            {PROVIDER_COPY[provider].label}
                          </span>
                          <span className="text-[10px] text-gray-400 truncate">
                            {PROVIDER_COPY[provider].hint}
                          </span>
                        </span>
                        <span
                          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
                            isSelected ? 'border-blue-500' : 'border-gray-300'
                          }`}>
                          {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div>
                  <p className="text-[11px] font-medium text-gray-600 mb-1">Inbox address</p>
                  <DillInput
                    value={newEmail}
                    onChange={setNewEmail}
                    placeholder="support@yourcompany.com"
                    error={newEmail.trim() && !isNewEmailValid ? 'Enter a valid email' : ''}
                    disabled={isConnectingInbox}
                  />
                </div>

                {configuredProviders[connectingProvider] === false ? (
                  <div className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2">
                    <Info size={13} className="text-amber-600 shrink-0 mt-px" />
                    <p className="text-[11px] text-amber-800">
                      {PROVIDER_COPY[connectingProvider].label} connections are not available yet.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-1.5">
                    <Info size={13} className="text-gray-400 shrink-0 mt-px" />
                    <p className="text-[11px] text-gray-400">
                      {`You'll sign in to this inbox with ${PROVIDER_COPY[connectingProvider].label} and grant access, then come back here.`}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <DillButton
                    label="Cancel"
                    variant="secondary"
                    onClick={handleCancelConnectInbox}
                    isDisabled={isConnectingInbox}
                  />
                  <DillButton
                    type="submit"
                    label={
                      isConnectingInbox
                        ? 'Redirecting…'
                        : `Continue with ${PROVIDER_COPY[connectingProvider].label}`
                    }
                    rightIcon={<ArrowRight size={13} />}
                    onClick={() => undefined}
                    isLoading={isConnectingInbox}
                    isDisabled={
                      !canEdit ||
                      !isNewEmailValid ||
                      configuredProviders[connectingProvider] === false
                    }
                  />
                </div>
              </form>
            )}

            {/* Loading State */}
            {isLoadingInboxes &&
              inboxes.length === 0 &&
              [0, 1].map((row) => (
                <div
                  key={row}
                  className="grid grid-cols-[1fr_96px_120px] items-center px-4 py-3 border-b border-gray-100 last:border-b-0 animate-pulse">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full bg-gray-200" />
                    <span className="h-2.5 w-40 rounded bg-gray-200" />
                  </div>
                  <span className="h-2.5 w-16 rounded bg-gray-200" />
                  <span />
                </div>
              ))}

            {/* Connected Inbox Rows */}
            {inboxes.map((inbox) => {
              const isRemoving = removingInboxId === inbox.id;
              if (confirmingRemoveInboxId === inbox.id) {
                return (
                  <div
                    key={inbox.id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 bg-red-50/50">
                    <div className="flex items-start gap-2 min-w-0">
                      <LinkBreak size={14} className="text-red-500 shrink-0 mt-px" />
                      <p className="text-xs text-gray-700">
                        Disconnect <span className="font-medium text-gray-900">{inbox.email}</span>?
                        <span className="block text-[11px] text-gray-500">
                          Dill AI will stop monitoring and replying from this inbox.
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <DillButton
                        label="Cancel"
                        variant="secondary"
                        height={28}
                        onClick={() => setConfirmingRemoveInboxId(null)}
                        isDisabled={isRemoving}
                      />
                      <DillButton
                        label={isRemoving ? 'Disconnecting…' : 'Disconnect'}
                        variant="danger"
                        height={28}
                        onClick={() => handleRemoveInbox(inbox)}
                        isLoading={isRemoving}
                        isDisabled={!canEdit}
                      />
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={inbox.id}
                  className="grid grid-cols-[1fr_96px_120px] items-center px-4 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                        PROVIDER_COPY[inbox.provider].badgeClass
                      }`}>
                      {PROVIDER_COPY[inbox.provider].letter}
                    </span>
                    <span className="text-xs text-gray-800 truncate">{inbox.email}</span>
                  </div>
                  {inbox.status === 'NEEDS_RECONNECT' ? (
                    <button
                      type="button"
                      title={inbox.lastError || 'Access expired. Sign in again to reconnect.'}
                      onClick={() => handleConfirmConnectInbox(inbox.provider, inbox.email)}
                      disabled={!canEdit || isConnectingInbox}
                      className="flex items-center gap-1.5 bg-transparent border-none p-0 cursor-pointer disabled:cursor-not-allowed">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${STATUS_COPY[inbox.status].dot}`}
                      />
                      <span className={`text-[11px] underline ${STATUS_COPY[inbox.status].text}`}>
                        {STATUS_COPY[inbox.status].label}
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${STATUS_COPY[inbox.status].dot}`}
                      />
                      <span className={`text-[11px] ${STATUS_COPY[inbox.status].text}`}>
                        {STATUS_COPY[inbox.status].label}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <span data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                      <button
                        type="button"
                        onClick={() => setConfirmingRemoveInboxId(inbox.id)}
                        disabled={!canEdit || removingInboxId !== null}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 bg-transparent border-none cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-500">
                        <LinkBreak size={12} />
                        Disconnect
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Empty State */}
            {inboxes.length === 0 && !connectingProvider && !isLoadingInboxes && (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-gray-400">No inboxes connected yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
